import { useEffect, useMemo, useState } from "react"
import useSWR from "swr"
import { useFrontispieces } from "./useFrontispieces"
import { fetchTieup } from "../../models/tieup/fetchTieup"

/** 最低挿絵数 */
export const LIMIT_PLATES_NUM = 0
/** 最低文字数 */
export const LIMIT_WORD_COUNT = 1
/** 最低口絵数 */
export const LIMIT_FRONTISPIECE_NUM = 0

/** 公開条件 */
export type TieupPublishState = {
  /** 挿絵数 */
  platesNum: boolean
  /** 文字数 */
  wordCount: boolean
  /** 口絵 */
  frontispiece: boolean
  /** 表示 */
  coverImage: boolean
  /** あらすじ */
  outlineText: boolean
}

export type TieupInfos = {
  platesNum: number
  wordCount: number
  frontispieceNum: number
}

const useTieup = (tieupId: number) => {
  const [publicState, setPublicState] = useState<TieupPublishState>({
    platesNum: false,
    wordCount: false,
    frontispiece: false,
    coverImage: false,
    outlineText: false,
  })

  const [tieupInfos, setTieupInfos] = useState<TieupInfos>({
    platesNum: 0,
    wordCount: 0,
    frontispieceNum: 0,
  })

  /** 公開条件を満たすかどうか */
  const IsSatisfiedPublishCondition = useMemo(() => {
    return Object.values(publicState).every((v) => v)
  }, [publicState])

  const key = `/v1/mypage/tieup/${tieupId}`
  /** たいあっぷ個別データ */
  const {
    data: tieup,
    error: tieupErrorResponse,
    mutate,
  } = useSWR(
    tieupId ? key : null,
    () => {
      return fetchTieup(tieupId)
    },
    {
      // 自動更新OFF
      revalidateOnFocus: false,
    },
  )

  /** 口絵 */
  const { frontispieces } = useFrontispieces(
    tieup?.novel?.id || tieup?.baseNovelId,
  )

  /** 更新 */
  const mutateTieup = () => {
    mutate()
  }

  useEffect(() => {
    if (!tieup || !tieup.novel) return
    const chapters = tieup.novel.chapters
    if (!chapters) return
    const platesNum = chapters.reduce((a, b) => a + b.plates.length, 0)
    const wordCount = chapters.reduce((a, b) => a + b.word_count, 0)
    setTieupInfos({
      platesNum,
      wordCount,
      frontispieceNum: frontispieces.length,
    })
  }, [tieup, frontispieces])

  useEffect(() => {
    if (!tieup || !tieup.novel) return

    const copiedState = { ...publicState }
    // 口絵
    copiedState.frontispiece =
      tieupInfos.frontispieceNum >= LIMIT_FRONTISPIECE_NUM
    // 挿絵
    copiedState.platesNum = tieupInfos.platesNum >= LIMIT_PLATES_NUM
    // 文字数
    copiedState.wordCount = tieupInfos.wordCount >= LIMIT_WORD_COUNT
    // あらすじ
    copiedState.outlineText = !!tieup.novel.outline
    // 表示
    copiedState.coverImage = !!tieup.novel.coverImage

    setPublicState(copiedState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tieup, tieupInfos])

  return {
    tieup: tieup,
    mutateTieup,
    IsSatisfiedPublishCondition,
    tieupInfos,
    publicState,
    tieupErrorResponse,
  }
}

export { useTieup }
